import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Tooltip as ReactTooltip } from "react-tooltip";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import { toast } from "../../helper/common";
import provincesData from "../../Utils/provincia_municipios.json";
import streetTypeCodes from "../../Utils/streetTypeCodes.json";
import moreInfoimg from "../../assets/img/moreInfoimg.svg";
import DatePicker from "react-datepicker";

// Matches the react-select look used across the app's compact form fields
const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "none",
    height: "28px",
    border: "0",
    boxShadow: "none",
    fontSize: "13.997px",
    padding: "0",
    cursor: "pointer",
    backgroundColor: "transparent",
    borderRadius: "0",
    "&:hover": { border: "0" },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "28px",
    padding: "0",
    margin: "0",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    gridArea: "1/1/2/3",
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: "#7f7f7f",
    gridArea: "1/1/2/3",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, gridArea: "1/1/2/3" }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const numericOnlyOnKeyDown = (e) => {
  const allowed = [
    "Backspace",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Enter",
  ];
  if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
};

const isValidDateOfBirth = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    year >= 1900 &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date <= today
  );
};

const dateOfBirthToDate = (value) => {
  if (!isValidDateOfBirth(value)) return null;
  const [day, month, year] = value.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const dateToDateOfBirth = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const WorkspaceDateOfBirthPicker = ({ value, onChange }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <div className="native-like-datepicker">
      <DatePicker
        selected={dateOfBirthToDate(value)}
        open={isCalendarOpen}
        onInputClick={() => setIsCalendarOpen(true)}
        onClickOutside={() => setIsCalendarOpen(false)}
        onSelect={() => setIsCalendarOpen(false)}
        onChange={(date) => onChange(dateToDateOfBirth(date))}
        onChangeRaw={(e) => {
          if (
            e.target.value &&
            (!e.nativeEvent.inputType ||
              !e.nativeEvent.inputType.startsWith("delete"))
          ) {
            let digits = e.target.value.replace(/\D/g, "").slice(0, 8);
            if (digits.length >= 5) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
            } else if (digits.length >= 3) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }
            e.target.value = digits;
          }
        }}
        dateFormat={["dd/MM/yyyy", "ddMMyyyy", "dd-MM-yyyy", "dd.MM.yyyy"]}
        minDate={new Date("1900-01-01")}
        maxDate={new Date()}
        placeholderText="dd/mm/yyyy"
        className={`date-input ${!value ? "empty" : ""} ${!isValidDateOfBirth(value) ? "input-error" : ""}`}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        openToDate={
          dateOfBirthToDate(value) || new Date(currentYear - 25, 0, 1)
        }
        renderCustomHeader={({ date, changeYear, changeMonth }) => (
          <div className="datepicker-custom-header">
            <div className="datepicker-header-left w-100 justify-content-between px-2">
              <div className="datepicker-selection-item">
                <span className="datepicker-current-title">
                  {date.toLocaleString("default", { month: "long" })}
                </span>
                <svg
                  className="datepicker-dropdown-arrow ms-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  style={{ width: "12px", height: "12px" }}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <select
                  value={date.getMonth()}
                  onChange={(e) => changeMonth(Number(e.target.value))}
                  className="datepicker-invisible-select"
                >
                  {Array.from({ length: 12 }, (_, month) => (
                    <option key={month} value={month}>
                      {new Date(0, month).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="datepicker-selection-item">
                <span className="datepicker-current-title">
                  {date.getFullYear()}
                </span>
                <svg
                  className="datepicker-dropdown-arrow ms-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  style={{ width: "12px", height: "12px" }}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <select
                  value={date.getFullYear()}
                  onChange={(e) => changeYear(Number(e.target.value))}
                  className="datepicker-invisible-select"
                >
                  {Array.from(
                    { length: currentYear - 1899 },
                    (_, index) => 1900 + index
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      >
        <div className="datepicker-footer">
          <button
            type="button"
            className="datepicker-footer-btn"
            onClick={() => {
              onChange("");
              setIsCalendarOpen(false);
            }}
          >
            Clear
          </button>
          <button
            type="button"
            className="datepicker-footer-btn"
            onClick={() => {
              onChange(dateToDateOfBirth(new Date()));
              setIsCalendarOpen(false);
            }}
          >
            Today
          </button>
        </div>
      </DatePicker>
      <span
        className="calendar-icon cursor-pointer"
        onClick={() => setIsCalendarOpen((prev) => !prev)}
      >
        <img src="/images/calendar-icon.svg" alt="Calendar Icon" />
      </span>
    </div>
  );
};

const PERSON_TYPE_OPTIONS = [
  { title: "individual", icon: "/images/user-icon.svg", key: "individual" },
  { title: "company", icon: "/images/building-icon.svg", key: "company" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: "selectDocumentType", value: "" },
  { label: "dni", value: "dni" },
  { label: "nie", value: "nie" },
  { label: "passport", value: "passport" },
];

// Mirrors PurchaseInsurance.jsx's FIELDS_INDIVIDUAL / FIELDS_COMPANY, minus contact fields
const FIELDS_INDIVIDUAL = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
  { label: "firstName", placeholder: "enterFirstName", name: "first_name" },
  { label: "lastName", placeholder: "enterLastName", name: "last_name" },
  {
    label: "dateofbirth",
    type: "date",
    name: "date_of_birth",
    class: "full-width",
  },
];

const FIELDS_COMPANY = [
  { label: "enterCif", placeholder: "enterCif", name: "document_number" },
  { label: "companyName", placeholder: "enterCompanyName", name: "first_name" },
];

const EditBeneficiaryModal = ({ show, onClose }) => {
  const { _t } = useTranslator();
  const { updateWorkspaceBeneficiary } = useContext(LandLordContext);
  const { user, loadUserTenant } = useContext(AuthContext);

  const saved = user?.current_workspace?.beneficiary_details?.[0] ?? {};

  const snapshot = {
    person_type: saved.person_type || "individual",
    document_type: saved.document_type || "",
    document_number: saved.document_number || "",
    first_name: saved.first_name || "",
    last_name: saved.last_name || "",
    date_of_birth: saved.date_of_birth || "",
    province: saved.province || "",
    city: saved.city || "",
    street_type: saved.street_type || "",
    street_name: saved.street_name || "",
    street_number: saved.street_number || "",
    postcode: saved.postcode || "",
    block: saved.block || "",
    ladder: saved.ladder || "",
    floor: saved.floor || "",
    door: saved.door || "",
    additional_address: saved.additional_address || "",
  };

  const [personType, setPersonType] = useState(snapshot.person_type);
  const [beneficiaryData, setBeneficiaryData] = useState({
    document_type: snapshot.document_type,
    document_number: snapshot.document_number,
    first_name: snapshot.first_name,
    last_name: snapshot.last_name,
    date_of_birth: snapshot.date_of_birth,
    province: snapshot.province,
    city: snapshot.city,
    street_type: snapshot.street_type,
    street_name: snapshot.street_name,
    street_number: snapshot.street_number,
    postcode: snapshot.postcode,
    block: snapshot.block,
    ladder: snapshot.ladder,
    floor: snapshot.floor,
    door: snapshot.door,
    additional_address: snapshot.additional_address,
  });
  const [saving, setSaving] = useState(false);
  const [postcodeTouched, setPostcodeTouched] = useState(false);

  const setField = (name, value) =>
    setBeneficiaryData((d) => ({ ...d, [name]: value }));

  const handleProvinceChange = (selected) =>
    setBeneficiaryData((d) => ({
      ...d,
      province: selected?.value || "",
      city: "",
    }));

  const handleFloorChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value !== "" && Number(value) > 40) value = "40";
    setField("floor", value);
  };

  const handlePersonTypeChange = (key) => {
    setPersonType(key);
    setBeneficiaryData((d) => ({
      ...d,
      document_type: key === "company" ? "cif" : "",
      document_number: "",
      first_name: "",
      last_name: "",
      date_of_birth: "",
    }));
  };

  const activeFields =
    personType === "company" ? FIELDS_COMPANY : FIELDS_INDIVIDUAL;

  const {
    document_type,
    document_number,
    first_name,
    last_name,
    date_of_birth,
    province,
    city,
    street_type,
    street_name,
    street_number,
    postcode,
    block,
    ladder,
    floor,
    door,
  } = beneficiaryData;

  const cityOptions = province
    ? provincesData
        .find((p) => p.Provincia === province)
        ?.municipios.map((m) => ({
          label: m,
          value: m,
        })) || []
    : [];

  const streetTypeValue = street_type
    ? (() => {
        const foundKey = Object.keys(streetTypeCodes).find(
          (key) => streetTypeCodes[key] === street_type
        );
        return {
          label: foundKey
            ? foundKey.charAt(0).toUpperCase() + foundKey.slice(1)
            : street_type,
          value: street_type,
        };
      })()
    : null;

  const postalCodeValid =
    postcode.trim() === "" || postcode.trim().length === 5;

  const addressComplete =
    [province, city, street_name, street_type, street_number].every(
      (v) => v.trim() !== ""
    ) && postcode.trim().length === 5;

  const isComplete =
    personType === "company"
      ? addressComplete &&
        document_number.trim() !== "" &&
        first_name.trim() !== ""
      : addressComplete &&
        document_type.trim() !== "" &&
        document_number.trim() !== "" &&
        first_name.trim() !== "" &&
        last_name.trim() !== "" &&
        isValidDateOfBirth(date_of_birth);

  const hasChanges =
    personType !== snapshot.person_type ||
    Object.keys(beneficiaryData).some(
      (key) => beneficiaryData[key] !== snapshot[key]
    );

  const canSave = isComplete && hasChanges && !saving;

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateWorkspaceBeneficiary({
        beneficiaries: [
          {
            person_type: personType,
            ...beneficiaryData,
          },
        ],
      });
      await loadUserTenant("landlord");
      toast.success(_t("beneficiaryDetailsSaved"));
      onClose?.();
    } catch (err) {
      console.log(err);
      toast.error(_t("errorWhileSavingBeneficiaryDetails"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="newmodalui max-w-lg tailwind-base-modal wrapper-modal newWorkspaceModal manageWorkspaceModalWrapper"
    >
      <Modal.Header>
        <div className="d-flex align-items-center justify-content-between w-100 beneficiary-modal-header">
          <h5 className="billing-tab__title d-flex align-items-center custom-gap-2 mb-0">
            <img
              src="/images/user-icon.svg"
              alt="User"
              width={22}
              height={22}
            />
            {_t("beneficiaryInformation")}
            <img
              data-tooltip-id="workspace-beneficiary-info-tooltip"
              src={moreInfoimg}
              alt="Vector Icon"
              className="vactorImg vactorImg1 default-check-img"
              style={{ filter: "none" }}
            />
            <ReactTooltip
              id="workspace-beneficiary-info-tooltip"
              noArrow
              className="react-tooltip-styling details-skor-tooltip"
              render={() => <p>{_t("workspaceBeneficiaryDetailsTooltip")}</p>}
            />
          </h5>
          <div className="beneficiary-card-actions">
            {PERSON_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`beneficiary-person-type-btn ${
                  personType === opt.key ? "active" : ""
                }`}
                onClick={() => handlePersonTypeChange(opt.key)}
              >
                <img src={opt.icon} alt="" />
                {_t(opt.title)}
              </button>
            ))}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="billing-tab">
        <div className="purchase-insurance-details-wrapper d-flex flex-column bigform">
          <h5 className="custom-mt-3">{_t("identification")}</h5>
          <div className="form-wrapper">
            {activeFields.map((field) => {
              const hasValue = !!beneficiaryData[field.name];
              return (
                <div
                  className={`main-form-group ${field.class || ""}`}
                  key={field.name}
                >
                  <div className="form-group">
                    <label>
                      {_t(field.label)}
                      <span className="required-star"> *</span>
                    </label>
                    {field.type === "select" ? (
                      <>
                        <select
                          name={field.name}
                          value={beneficiaryData[field.name]}
                          onChange={(e) => setField(field.name, e.target.value)}
                          className={`${!hasValue ? "select-empty input-error" : ""}`}
                        >
                          {field.options.map((option) => (
                            <option value={option.value} key={option.value}>
                              {_t(option.label)}
                            </option>
                          ))}
                        </select>
                        <img
                          src="/images/down-arrow-grey.svg"
                          className="downArrow"
                          alt="arrow"
                        />
                      </>
                    ) : field.type === "date" ? (
                      <WorkspaceDateOfBirthPicker
                        value={beneficiaryData[field.name]}
                        onChange={(value) => setField(field.name, value)}
                      />
                    ) : (
                      <input
                        type="text"
                        name={field.name}
                        placeholder={_t(field.placeholder)}
                        value={beneficiaryData[field.name]}
                        onChange={(e) => setField(field.name, e.target.value)}
                        className={!hasValue ? "input-error" : ""}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <h5>{_t("address")}</h5>
          <div className="form-wrapper">
            <div className="main-form-group" id="custom-select-search">
              <div className="form-group">
                <label>
                  {_t("province")} <span className="required-star">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  name="province"
                  value={province ? { label: province, value: province } : null}
                  options={provincesData.map((p) => ({
                    label: p.Provincia,
                    value: p.Provincia,
                  }))}
                  placeholder={_t("selectProvince")}
                  styles={compactSelectStyles}
                  onChange={handleProvinceChange}
                  isSearchable
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }}
                />
                <img
                  src="/images/down-arrow-grey.svg"
                  className="downArrow"
                  alt="arrow"
                />
              </div>
            </div>

            <div className="main-form-group" id="custom-select-search">
              <div className="form-group">
                <label>
                  {_t("municipality")} <span className="required-star">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  name="city"
                  value={city ? { label: city, value: city } : null}
                  options={cityOptions}
                  placeholder={
                    province
                      ? _t("selectMunicipality")
                      : _t("selectProvinceFirst")
                  }
                  onChange={(selected) =>
                    setField("city", selected?.value || "")
                  }
                  styles={compactSelectStyles}
                  isSearchable
                  isDisabled={!province}
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }}
                />
                <img
                  src="/images/down-arrow-grey.svg"
                  className="downArrow"
                  alt="arrow"
                />
              </div>
            </div>

            <div className="main-form-group" id="custom-select-search">
              <div className="form-group">
                <label>
                  {_t("typeOfRoad")} <span className="required-star">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  name="street_type"
                  value={streetTypeValue}
                  options={Object.keys(streetTypeCodes).map((type) => ({
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                    value: streetTypeCodes[type],
                  }))}
                  placeholder={_t("selectType")}
                  onChange={(selected) =>
                    setField("street_type", selected?.value || "")
                  }
                  styles={compactSelectStyles}
                  isSearchable
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }}
                />
                <img
                  src="/images/down-arrow-grey.svg"
                  className="downArrow"
                  alt="arrow"
                />
              </div>
            </div>

            <div className="main-form-group">
              <div className="form-group">
                <label>
                  {_t("streetName")} <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="street_name"
                  placeholder={_t("enterStreetName")}
                  value={street_name}
                  onChange={(e) => setField("street_name", e.target.value)}
                  className={!street_name ? "input-error" : ""}
                />
              </div>
            </div>

            <div className="main-form-group">
              <div className="form-group">
                <label>
                  {_t("number")} <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="street_number"
                  placeholder={_t("eG5")}
                  value={street_number}
                  onChange={(e) => setField("street_number", e.target.value)}
                  onKeyDown={numericOnlyOnKeyDown}
                  className={!street_number ? "input-error" : ""}
                />
              </div>
            </div>

            <div className="main-form-group">
              <div className="form-group">
                <label>
                  {_t("postalCode")} <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="postcode"
                  placeholder="28006"
                  value={postcode}
                  onChange={(e) => setField("postcode", e.target.value)}
                  onKeyDown={numericOnlyOnKeyDown}
                  onBlur={() => setPostcodeTouched(true)}
                  maxLength={5}
                  className={!postcode ? "input-error" : ""}
                />
              </div>
              {postcodeTouched && !postalCodeValid && (
                <span className="error-message">
                  {_t("postalCodeMustBe5Digits")}
                </span>
              )}
            </div>
          </div>

          <div className="beneficiary-grid-4">
            <div className="form-group">
              <label>{_t("block")}</label>
              <input
                type="text"
                name="block"
                value={block}
                onChange={(e) => setField("block", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{_t("ladder")}</label>
              <input
                type="text"
                name="ladder"
                value={ladder}
                onChange={(e) => setField("ladder", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{_t("floor")}</label>
              <input
                type="text"
                name="floor"
                value={floor}
                onChange={handleFloorChange}
                onKeyDown={numericOnlyOnKeyDown}
              />
            </div>
            <div className="form-group">
              <label>{_t("door")}</label>
              <input
                type="text"
                name="door"
                value={door}
                onChange={(e) => setField("door", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="billing-tab__footer inviteButtonWrapper">
          <button
            type="button"
            className="btnNext btnnextpre mr-2"
            onClick={onClose}
          >
            {_t("cancel")}
          </button>
          <button
            type="button"
            className="btnNext greenHover"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? `${_t("saving")}...` : _t("save")}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EditBeneficiaryModal;
